import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { 
  FiArrowLeft, FiUser, FiMail, FiPhone, FiCalendar, 
  FiMapPin, FiEdit3, FiSave, FiX, FiTrash2, FiShield,
  FiTruck, FiDollarSign, FiStar, FiClock, FiCheckCircle,
  FiXCircle, FiAlertCircle, FiActivity,
  FiLock, FiUnlock, FiDownload, FiUserX
} from 'react-icons/fi';
import UserRegistrationDetailsView from './UserRegistrationDetailsView';
import { resolveUserPhone, formatUserPhoneDisplay } from '../../utils/resolveUserPhone';
import { isTempLibertyEmail, publicEmail } from '../../utils/emailUtils';

// Formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Formater le montant
const formatPrice = (cents) => {
  if (!cents) return '0,00 €';
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
};

// Obtenir la couleur du statut
const getStatusColor = (status) => {
  switch(status) {
    case 'confirmé':
    case 'completed':
      return 'bg-green-600/20 text-green-400 border border-green-600/50';
    case 'en_attente':
    case 'pending':
      return 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50';
    case 'annulé':
    case 'cancelled':
      return 'bg-red-600/20 text-red-400 border border-red-600/50';
    default:
      return 'bg-blue-600/20 text-blue-400 border border-blue-600/50';
  }
};

function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({
    totalReservations: 0,
    totalSpent: 0,
    averageRating: 0,
    completedMoves: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'client',
    is_verified: false,
    phone_verified: false,
    status: 'available',
  });
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [registrationPayload, setRegistrationPayload] = useState(null);

  useEffect(() => {
    if (id) {
      loadUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getUserDetails(id);

      if (response && response.success) {
        // API: soit `{ data: user }`, soit `{ data: { user, reservations, stats } }`
        const payload = response.data || {};
        const userRecord = payload.user ?? payload;
        if (!userRecord || typeof userRecord !== 'object' || !userRecord.id) {
          throw new Error('Données utilisateur invalides ou incomplètes');
        }
        const resolvedPhone = resolveUserPhone(userRecord) || '';
        const normalizedUser = { ...userRecord, phone: resolvedPhone };
        setUser(normalizedUser);
        setReservations(payload.reservations || []);
        setStats(
          payload.stats || {
            totalReservations: 0,
            totalSpent: 0,
            averageRating: 0,
            completedMoves: 0
          }
        );
        setFormData({
          first_name: normalizedUser.first_name || '',
          last_name: normalizedUser.last_name || '',
          email: isTempLibertyEmail(normalizedUser.email) ? '' : (normalizedUser.email || ''),
          phone: resolvedPhone,
          role: normalizedUser.role || 'client',
          is_verified: !!normalizedUser.is_verified,
          phone_verified: !!normalizedUser.phone_verified,
          status: normalizedUser.status || 'available',
        });

        if (userRecord.role === 'demenageur') {
          try {
            const regRes = await api.getMoverRegistrationDetails(id);
            if (regRes?.success && regRes.data) {
              setRegistrationPayload(regRes.data);
            } else {
              setRegistrationPayload(null);
            }
          } catch (regErr) {
            console.warn('Détails inscription déménageur:', regErr);
            setRegistrationPayload(null);
          }
        } else {
          setRegistrationPayload(null);
        }
      } else {
        throw new Error(response.message || 'Erreur lors du chargement des détails');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      setError(err.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(null);

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: formData.role,
        is_verified: formData.is_verified,
        phone_verified: formData.phone_verified,
        status: formData.status,
      };
      const trimmedEmail = String(formData.email || '').trim();
      if (trimmedEmail && !isTempLibertyEmail(trimmedEmail)) {
        payload.email = trimmedEmail;
      }

      const response = await api.updateUser(id, payload);
      if (response && response.success) {
        setIsEditing(false);
        setSaveSuccess(response.message || 'Modifications enregistrées');
        await loadUserDetails();
      } else {
        throw new Error(response?.message || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.first_name} ${user.last_name} ? Cette action est irréversible.`)) {
      return;
    }
    try {
      const response = await api.deleteUser(id);
      if (response && response.success) {
        navigate('/admin/users');
      } else {
        throw new Error(response.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleBan = async () => {
    setShowBanModal(true);
  };

  const confirmBan = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir bannir l'utilisateur ${user.first_name} ${user.last_name} ?`)) {
      return;
    }
    
    try {
      setSaving(true);
      setShowBanModal(false);
      const response = await api.banUser(id, banReason || '');
      if (response && response.success) {
        setBanReason('');
        loadUserDetails();
      } else {
        throw new Error(response.message || 'Erreur lors du bannissement');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du bannissement');
    } finally {
      setSaving(false);
    }
  };

  const handleUnban = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir débannir l'utilisateur ${user.first_name} ${user.last_name} ?`)) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await api.unbanUser(id);
      if (response && response.success) {
        loadUserDetails();
      } else {
        throw new Error(response.message || 'Erreur lors du débannissement');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du débannissement');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      client: 'bg-blue-600/20 text-blue-400 border border-blue-600/50',
      demenageur: 'bg-purple-600/20 text-purple-400 border border-purple-600/50',
      admin: 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/50'
    };
    return badges[role] || 'bg-gray-700 text-gray-300 border border-gray-600';
  };

  const getRoleLabel = (role) => {
    const labels = {
      client: 'Client',
      demenageur: 'Déménageur',
      admin: 'Administrateur'
    };
    return labels[role] || role;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-yellow-500 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-6">
          <div className="flex items-center">
            <FiAlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-300">Erreur</h3>
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        </div>
        <Link
          to="/admin/users"
          className="inline-flex items-center px-4 py-2 bg-yellow-600 text-gray-900 text-yellow-400 rounded-lg hover:bg-yellow-500 transition-all"
        >
          <FiArrowLeft className="mr-2" />
          Retour à la liste
        </Link>
      </div>
    );
  }

return (
  <div className="space-y-6">
    {/* Main Content */}
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin/users"
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-6 h-6 text-gray-400" />
          </Link>
          <div>
            <h1 className="text-4xl font-bold text-yellow-400">
              Détails de l'utilisateur
            </h1>
            <p className="text-gray-400 font-medium mt-1">
              Informations complètes et historique
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold flex items-center shadow-lg shadow-yellow-500/30 disabled:opacity-50"
              >
                <FiSave className="mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    first_name: user.first_name,
                    last_name: user.last_name,
                    email: isTempLibertyEmail(user.email) ? '' : (user.email || ''),
                    phone: user.phone,
                    role: user.role,
                    is_verified: !!user.is_verified,
                    phone_verified: !!user.phone_verified,
                    status: user.status || 'available',
                  });
                }}
                className="px-4 py-2 border border-gray-700 rounded-lg hover:bg-gray-700 transition-all font-semibold"
              >
                <FiX className="mr-2 inline" />
                Annuler
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-all font-semibold flex items-center shadow-lg shadow-yellow-500/30"
              >
                <FiEdit3 className="mr-2" />
                Modifier
              </button>
              {user?.is_banned ? (
                <button
                  onClick={handleUnban}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold flex items-center disabled:opacity-50"
                >
                  <FiUnlock className="mr-2" />
                  Débannir
                </button>
              ) : (
                <button
                  onClick={handleBan}
                  disabled={saving || user?.is_admin}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-semibold flex items-center disabled:opacity-50"
                  title={user?.is_admin ? 'Impossible de bannir un administrateur' : ''}
                >
                  <FiUserX className="mr-2" />
                  Bannir
                </button>
              )}
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold flex items-center"
              >
                <FiTrash2 className="mr-2" />
                Supprimer
              </button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && (
        <div className="bg-green-900/30 border-l-4 border-green-500 rounded-lg p-4">
          <div className="flex items-center">
            <FiCheckCircle className="h-5 w-5 text-green-500 mr-3" />
            <p className="text-sm font-semibold text-green-300">{saveSuccess}</p>
            <button onClick={() => setSaveSuccess(null)} className="ml-auto text-green-500 hover:text-green-300">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <FiAlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <p className="text-sm font-semibold text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-300">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* User Info Card */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gray-900 border-b border-yellow-600/30 px-8 py-6">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-yellow-600/20 border border-yellow-600/50 rounded-2xl flex items-center justify-center text-yellow-400 text-4xl font-bold shadow-xl">
              {getInitials(user?.first_name, user?.last_name)}
            </div>
            <div className="flex-1 text-yellow-400">
              <h2 className="text-3xl font-bold mb-2">
                {user?.first_name} {user?.last_name}
              </h2>
              <div className="flex items-center space-x-4 text-yellow-400/80">
                <div className="flex items-center">
                  <FiMail className="mr-2" />
                  {publicEmail(user?.email) || 'Inscription par téléphone'}
                </div>
                <div className="flex items-center">
                  <FiPhone className="mr-2" />
                  {formatUserPhoneDisplay(user)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-4 py-2 inline-flex text-sm font-bold rounded-full shadow-lg ${getRoleBadge(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </span>
              {user?.is_admin && (
                <div className="mt-2 flex items-center justify-end text-yellow-400/80">
                  <FiShield className="mr-1" />
                  <span className="text-sm">Admin</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Informations de base communes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Prénom</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500"
                />
              ) : (
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">{user?.first_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Nom</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500"
                />
              ) : (
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">{user?.last_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder={isTempLibertyEmail(user?.email) ? 'Ajouter un email (optionnel)' : 'email@exemple.com'}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500"
                />
              ) : (
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white flex items-center">
                  <FiMail className="mr-2 text-gray-400" />
                  {publicEmail(user?.email) || (
                    <span className="text-gray-400">Inscription par téléphone (pas d&apos;email)</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Téléphone</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500"
                />
              ) : (
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white flex items-center">
                  <FiPhone className="mr-2 text-gray-400" />
                  {formatUserPhoneDisplay(user)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Rôle</label>
              {isEditing ? (
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                >
                  <option value="client" style={{ backgroundColor: '#111827', color: '#ffffff' }}>Client</option>
                  <option value="demenageur" style={{ backgroundColor: '#111827', color: '#ffffff' }}>Déménageur</option>
                  <option value="admin" style={{ backgroundColor: '#111827', color: '#ffffff' }}>Administrateur</option>
                </select>
              ) : (
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  <span className={`px-3 py-1 inline-flex text-xs font-bold rounded-full ${getRoleBadge(user?.role)}`}>
                    {getRoleLabel(user?.role)}
                  </span>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Date d'inscription</label>
              <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg flex items-center text-white">
                <FiCalendar className="mr-2 text-gray-400" />
                {formatDate(user?.created_at)}
              </p>
            </div>
            {user?.address && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white flex items-center">
                  <FiMapPin className="mr-2 text-gray-400" />
                  {user.address}
                </p>
              </div>
            )}
            {user?.is_verified !== undefined && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Compte vérifié</label>
                {isEditing ? (
                  <label className="flex items-center gap-3 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.is_verified}
                      onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-white">Marquer comme vérifié</span>
                  </label>
                ) : (
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {user.is_verified ? (
                      <span className="flex items-center text-green-400 font-semibold">
                        <FiCheckCircle className="mr-2" />
                        Vérifié
                      </span>
                    ) : (
                      <span className="flex items-center text-yellow-400 font-semibold">
                        <FiAlertCircle className="mr-2" />
                        Non vérifié
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
            {user?.is_online !== undefined && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Statut en ligne</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                  {user.is_online ? (
                    <span className="flex items-center text-green-400 font-semibold">
                      <FiActivity className="mr-2" />
                      En ligne
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-400 font-semibold">
                      <FiClock className="mr-2" />
                      Hors ligne
                    </span>
                  )}
                </p>
              </div>
            )}
            {(user?.status || isEditing) && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Statut</label>
                {isEditing ? (
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="available">available</option>
                    <option value="inactive">inactive</option>
                    <option value="busy">busy</option>
                  </select>
                ) : (
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    <span className="px-3 py-1 inline-flex text-xs font-bold rounded-full bg-blue-600/20 text-blue-400 border border-blue-600/50">
                      {user.status}
                    </span>
                  </p>
                )}
              </div>
            )}
            {user?.provider && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Connexion via</label>
                <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white capitalize">{user.provider}</p>
              </div>
            )}
          </div>

          {/* Informations spécifiques CLIENT */}
          {user?.role === 'client' && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <FiUser className="mr-2 text-yellow-400" />
                Données du formulaire d&apos;inscription (client)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Numéro de téléphone</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white flex items-center">
                    <FiPhone className="mr-2 text-gray-400 shrink-0" />
                    {formatUserPhoneDisplay(user)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Mode d&apos;inscription</label>
                  <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                    {/^phone_\d+@temp\.liberty$/i.test(String(user.email || '').trim())
                      ? 'Téléphone (SMS)'
                      : user.phone_verified
                        ? 'Email avec téléphone vérifié'
                        : 'Email'}
                  </p>
                </div>
                {user.address && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Adresse complète</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                      {user.address}
                    </p>
                  </div>
                )}
                
                {user.stripe_customer_id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">ID Client Stripe</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-xs">
                      {user.stripe_customer_id}
                    </p>
                  </div>
                )}
                
                {user.default_payment_method_id && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Méthode de paiement par défaut</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs">
                      {user.default_payment_method_id || 'Aucune'}
                    </p>
                  </div>
                )}
                
                {user.phone_verified !== undefined && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Téléphone vérifié</label>
                    {isEditing ? (
                      <label className="flex items-center gap-3 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.phone_verified}
                          onChange={(e) => setFormData({ ...formData, phone_verified: e.target.checked })}
                          className="w-5 h-5 rounded"
                        />
                        <span className="text-white">Téléphone vérifié</span>
                      </label>
                    ) : (
                      <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                        {user.phone_verified ? (
                          <span className="flex items-center text-green-400 font-semibold">
                            <FiCheckCircle className="mr-2" />
                            Vérifié
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-400 font-semibold">
                            <FiXCircle className="mr-2" />
                            Non vérifié
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}
                
                {user.email_verified !== undefined && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Email vérifié</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                      {user.email_verified ? (
                        <span className="flex items-center text-green-400 font-semibold">
                          <FiCheckCircle className="mr-2" />
                          Vérifié
                        </span>
                      ) : (
                        <span className="flex items-center text-yellow-400 font-semibold">
                          <FiXCircle className="mr-2" />
                          Non vérifié
                        </span>
                      )}
                    </p>
                  </div>
                )}
                
                {user.two_factor_enabled !== undefined && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Authentification à 2 facteurs</label>
                    <p className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white">
                      {user.two_factor_enabled ? (
                        <span className="flex items-center text-green-400 font-semibold">
                          <FiLock className="mr-2" />
                          Activée
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-400 font-semibold">
                          <FiUnlock className="mr-2" />
                          Désactivée
                        </span>
                      )}
                    </p>
                  </div>
                )}
                
                {user.metadata && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Métadonnées</label>
                    <div className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
                      <pre className="text-xs text-gray-400 whitespace-pre-wrap">
                        {JSON.stringify(user.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {user?.role === 'demenageur' && (
            <UserRegistrationDetailsView payload={registrationPayload} userId={id} />
          )}
        </div>
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-gray-900 border border-yellow-600/30 rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-gray-800 border-b border-yellow-600/30 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-yellow-400">Bannir l'utilisateur</h2>
              <button 
                onClick={() => {
                  setShowBanModal(false);
                  setBanReason('');
                }} 
                className="text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Raison du bannissement (optionnel)
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Entrez la raison du bannissement..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder-gray-500 resize-none"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={confirmBan}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50"
                >
                  {saving ? 'Bannissement...' : 'Confirmer le bannissement'}
                </button>
                <button
                  onClick={() => {
                    setShowBanModal(false);
                    setBanReason('');
                  }}
                  className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default UserDetails;

